/**
 * A fresh career (plan §6 M2-01): docked at `career.json start.station` with the stock Kestrel fit, the start
 * wallet and standings, and a new lease from `lease.json` (first payment due one cycle of flight away).
 */
import type { M2Data } from '../data/m2Schemas'
import { HULK_TABLE_FIRST, hulksConfig, latestHulkTable } from '../hulks/data'
import { holdCapacityT } from './hullFit'
import { emptyWares } from './ops'
import type { Career } from './types'

/**
 * M10 P3b2 (plan question 5): the new galaxy records the build's newest hulk placement table, but only when it is
 * above 1 (absent = 1). While the build has table 1 only nothing is written, so career saves stay byte-identical and
 * a build from before P3b2 (strict career schema) still loads them.
 */
export function newCareer(data: M2Data, seed: number): Career {
  const start = data.career.start
  const lease = data.lease
  const table = latestHulkTable(hulksConfig())
  return {
    version: 1,
    seed: seed >>> 0,
    gameTime: 0,
    wallet: start.wallet,
    debt: 0,
    lease: {
      principal: lease.principal,
      ratePerCycle: lease.ratePerCycle,
      payment: lease.payment,
      cycleSeconds: lease.cycleSeconds,
      nextDueAt: lease.cycleSeconds,
      missed: 0,
      arrears: 0,
      feesDue: 0,
      collections: 'none',
      repoPausedUntil: 0,
      owned: false,
      insured: false,
      history: [],
    },
    // M10 P5a6a: the hull-rated hold of the start ship (`hullFit.ts holdCapacityT`; the rack's own with no `fit`).
    cargo: { capacityT: holdCapacityT(start.ship, start.fitted, data), wares: emptyWares(), components: [] },
    ship: { defId: start.ship, hull: start.hull, torpedoes: start.torpedoes, fitted: { ...start.fitted }, locker: [] },
    standings: { ...start.standings },
    contracts: { active: [], completedIds: [], failedIds: [] },
    dataCores: [],
    lastDockedStation: start.station,
    stats: { tetherstoneT: 0, oreT: 0, partsSecured: 0, deaths: 0, earned: 0 },
    ...(table !== HULK_TABLE_FIRST ? { hulkTable: table } : {}),
  }
}
